
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Check, X, Clock, Eye, MapPin, Truck } from "lucide-react";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
 } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

interface DeliveryItem {
  id: string;
  productName: string;
  quantity: number;
}

interface Delivery {
  id: string;
  orderId: string;
  orderDate: string;
  expectedDelivery: string;
  items: DeliveryItem[];
  totalItems: number;
  status: "In Transit" | "Out for Delivery" | "Ready for Pickup";
  deliveryPartner: string;
  trackingNumber?: string;
}

const PendingDeliveries: React.FC = () => {
  const pendingDeliveries: Delivery[] = [
    {
      id: "DEL-123456",
      orderId: "ORD-789012",
      orderDate: "2025-05-11",
      expectedDelivery: "2025-05-14",
      items: [
        { id: "1", productName: "Fresh Milk", quantity: 10 },
        { id: "2", productName: "White Bread", quantity: 15 },
        { id: "8", productName: "Sugar", quantity: 5 },
      ],
      totalItems: 3,
      status: "In Transit",
      deliveryPartner: "JiMAT Express",
      trackingNumber: "JEXP-1234567890"
    },
    {
      id: "DEL-123457",
      orderId: "ORD-789013",
      orderDate: "2025-05-12",
      expectedDelivery: "2025-05-13",
      items: [
        { id: "3", productName: "Eggs", quantity: 8 },
        { id: "4", productName: "Milo", quantity: 5 },
      ],
      totalItems: 2,
      status: "Out for Delivery",
      deliveryPartner: "JiMAT Express",
      trackingNumber: "JEXP-1234567891"
    },
    {
      id: "DEL-123458",
      orderId: "ORD-789014",
      orderDate: "2025-05-12",
      expectedDelivery: "2025-05-12",
      items: [
        { id: "5", productName: "Cooking Oil", quantity: 6 },
        { id: "6", productName: "Rice", quantity: 3 },
        { id: "7", productName: "Canned Tuna", quantity: 12 },
      ],
      totalItems: 3,
      status: "Ready for Pickup",
      deliveryPartner: "Self Pickup",
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "In Transit":
        return <Truck className="h-4 w-4 text-blue-500" />;
      case "Out for Delivery":
        return <MapPin className="h-4 w-4 text-amber-500" />;
      case "Ready for Pickup":
        return <Check className="h-4 w-4 text-green-500" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "In Transit":
        return "bg-blue-100 text-blue-800";
      case "Out for Delivery":
        return "bg-amber-100 text-amber-800";
      case "Ready for Pickup":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Pending Deliveries</h3>
      
      <Card className="border-green-100">
        <CardContent className="p-4">
          {pendingDeliveries.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-muted-foreground">No pending deliveries</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-green-50">
                    <TableHead>Delivery ID</TableHead>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Order Date</TableHead>
                    <TableHead>Expected Delivery</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Delivery Partner</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingDeliveries.map((delivery) => (
                    <TableRow key={delivery.id}>
                      <TableCell className="font-medium">{delivery.id}</TableCell>
                      <TableCell>{delivery.orderId}</TableCell>
                      <TableCell>{delivery.orderDate}</TableCell>
                      <TableCell>{delivery.expectedDelivery}</TableCell>
                      <TableCell>{delivery.totalItems} items</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`flex w-fit items-center space-x-1 ${getStatusColor(delivery.status)}`}>
                          {getStatusIcon(delivery.status)}
                          <span>{delivery.status}</span>
                        </Badge>
                      </TableCell>
                      <TableCell>{delivery.deliveryPartner}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Eye className="h-4 w-4 mr-1" />
                                Details
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Delivery Details</DialogTitle>
                                <DialogDescription>
                                  Delivery ID: {delivery.id}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <h4 className="font-medium">Order Information</h4>
                                  <p>Order ID: {delivery.orderId}</p>
                                  <p>Order Date: {delivery.orderDate}</p>
                                  <p>Expected Delivery: {delivery.expectedDelivery}</p>
                                </div>
                                <div>
                                  <h4 className="font-medium">Status</h4>
                                  <Badge variant="outline" className={`flex w-fit items-center space-x-1 ${getStatusColor(delivery.status)}`}>
                                    {getStatusIcon(delivery.status)}
                                    <span>{delivery.status}</span>
                                  </Badge>
                                </div>
                                {delivery.trackingNumber && (
                                  <div>
                                    <h4 className="font-medium">Tracking</h4>
                                    <p>Delivery Partner: {delivery.deliveryPartner}</p>
                                    <p>Tracking Number: {delivery.trackingNumber}</p>
                                  </div>
                                )}
                                <div>
                                  <h4 className="font-medium">Items</h4>
                                  <ul className="list-disc pl-5 space-y-1">
                                    {delivery.items.map(item => (
                                      <li key={item.id}>
                                        {item.productName} × {item.quantity}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                              <div className="flex justify-end mt-4">
                                <DialogClose asChild>
                                  <Button>Close</Button>
                                </DialogClose>
                              </div>
                            </DialogContent>
                          </Dialog>
                          
                          {delivery.status === "Ready for Pickup" && (
                            <Button size="sm" className="bg-green-600 hover:bg-green-700">
                              <Check className="h-4 w-4 mr-1" />
                              Mark Received
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PendingDeliveries;
